import React from 'react';
import { getContext, getEasing, getDuration, isAnimationDisabled } from 'common';
import { useSpring, animated } from 'react-spring';
import _merge from 'lodash/merge';
import { SPEED_FAST } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import { OPACITY_0 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/opacity';
import { TRANSITION_DECELERATE } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';
import Styled from './styles';

const SignInContext = React.createContext<any>({});

export interface SignInProps extends React.ComponentPropsWithRef<'div'> {
  /** Place login sub-components here. See sub-components and demo example for composition. */
  children: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** Set to true, the SignIn component will animate out. Use this in conjunction with LaunchPad `animateIn` to animate between these two components. */
  animateOut?: boolean;
  /** Triggered when the `animateOut` is finished. Useful in transitioning in the Launchpad.  */
  onAnimationComplete?(...args: unknown[]): unknown;
  /** Control animation config here. This uses React-spring config. See https://react-spring.io for more information. */
  animationProps?: any;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** To customize the wrapper style. */
  style?: React.CSSProperties
}

/** SignIn component features the login process and various related views.  */
const SignIn = React.forwardRef<HTMLElement, SignInProps>((props: SignInProps, ref) =>
  getContext(({ RTL, theme: globalTheme = {}, disableAnimation }) => {
    const {
      children, animateOut, animationProps, onAnimationComplete, disableAnimation: propsdisableAnimation, style, theme, ...otherProps
    } = props;
    const resolvedTheme = _merge({}, globalTheme.SIGN_IN, theme);
    const springProps = useSpring(animationProps || {
      opacity: animateOut ? OPACITY_0 : 1,
      background: animateOut ? globalTheme.LAUNCHPAD.innerBackground : resolvedTheme.background,
      delay: 2000,
      config: {
        duration: getDuration(SPEED_FAST),
        easing: getEasing(TRANSITION_DECELERATE)
      },
      onRest: (animation) => {
        if (animation.opacity === OPACITY_0) {
          onAnimationComplete && onAnimationComplete();
        }
      },
      immediate: isAnimationDisabled(propsdisableAnimation, disableAnimation),
    });
    const resolvedStyles = _merge({}, springProps, style);
    return (
      <Styled.SignInContainer as={animated.div} style={resolvedStyles} theme={resolvedTheme} {...otherProps}>
        <Styled.SignIn theme={resolvedTheme} $rtl={RTL} ref={ref}>
          <SignInContext.Provider value={{ animateOut }}>{children}</SignInContext.Provider>
        </Styled.SignIn>
      </Styled.SignInContainer>
    );
  }));

SignIn.defaultProps = {
  theme: {},
  animateOut: false,
  onAnimationComplete: undefined,
  animationProps: undefined,
  disableAnimation: undefined,
  style: {}
};
SignIn.displayName = 'SignIn';

/* Exports */
export { SignIn as default, SignInContext };
